import type { HttpQueryParameters } from '../util/apiClient';
import { isObject } from '@mimir/primitive/type-guards/isObject';
import type { GeoShape } from './GeoLocation';
import type { SearchItemType} from './itemTypes';
import { searchItemTypeList} from './itemTypes';
import type { MediaAvailabilityStatusType } from './mediaDtos';
import { mediaAvailabilityStatusList } from './mediaDtos';

/**
 * How search results are classified and aggregated in the side bar.
 *
 * This is not the type identifier for entities in the Item DynamoDB table.
 * For that, see DynamoItemType.
 *
 * This is also not the type identifier for _source documents in our item full
 * text search index. For that, see SearchItemType.
 */
export type SearchCategory = SearchItemType | MediaAvailabilityStatusType;
export const searchCategoryList: SearchCategory[] = [...searchItemTypeList, ...mediaAvailabilityStatusList];

export function isSearchCategory(input: string): input is SearchCategory {
  return searchCategoryList.includes(input as SearchCategory);
}

// SearchCategoryAlias is a type used in the UI to treat 'clipList' and 'timeline'
// as a single logic 'sequence' search filter
export const searchCategoryAliasList = <const>[
  ...searchCategoryList.filter(c => c !== 'clipList' && c !== 'timeline'),
  'sequence',
];
export type SearchCategoryAlias = typeof searchCategoryAliasList[number];


export function getSearchCategoryAlias(c: SearchCategory): SearchCategoryAlias {
  if (c === 'clipList' || c === 'timeline') {
    return 'sequence';
  }

  return c;
}

export function hasSearchCategoryOnly(input: string) {
  return input.split(',').every(i => isSearchCategory(i));
}

export interface AggregationBucketBase {
  doc_count: number;
}

export interface ItemTypeCount extends AggregationBucketBase {
  key: SearchItemType;
}
export interface SearchCategoryCount extends AggregationBucketBase {
  key: SearchCategory;
}

export interface RouterLink {
  name: string;
  params: Record<string, any>;
  query: HttpQueryParameters;
}

/**
 * Represents a search hit segment within an item. The thumbnail is used
 * for clicking on labels in a search result hit.
 */
export interface SearchHitSegment {
  start: number;
  end: number;
  thumbnail?: string;
  thumbnailStrip?: string;
  routerLink?: RouterLink;
  maxConfidence: number;
  avgConfidence?: number;
  imageId?: string;
  tab?: string;
}


/**
 * The different types of filters that our search supports.
 *
 * Note: This type says nothing about the actual keywords used to refer to the
 * different types of filters: Originally the FilterType was tied directly to
 * the corresponding keyword used for a filter, which meant that filters such as
 * "someMdfFieldId:foo" could not be represented as a "FilterType", since field
 * ids are not statically known.
 *
 * TODO: Improve documentation on the individual filters here.
 */
export type FilterType =
  'filter:label' |
  'filter:title' |
  'filter:person' |
  'filter:profileId' |
  'filter:transcript' |
  'filter:timedTranslatedTranscript' |
  'filter:description' |
  'filter:formId' |
  'filter:mediaCreatedOn' |
  'filter:createdDate' |
  'filter:scheduledRecording.startTimeMs' |
  'filter:modifiedOn' |
  'filter:geoLocation' |
  'filter:itemType' |
  'filter:itemState' |

  /**
   * For searching in any log event value across all schemas
   */
  'filter:events' |

  /**
   * For searching in any log event value in a given timed mdf schema.
   * The filter key needs to be the schema formId.
   */
  'filter:eventsForm' |

  /**
   * For searching in a specific log event field.
   */
  'filter:eventsField' |

  'filter:text' |
  'filter:folder' |
  'filter:fileName' |
  'filter:archived' |
  'filter:orphan' |
  'filter:sourceBucket' |
  'filter:folderId' |
  'filter:itemId' |
  'filter:purgeDate' |
  'filter:vclip' |
  'filter:owner' |
  'filter:rule' |
  'filter:ruleContext' |
  'filter:ruleValidity' |

  /**
   * For searching in any metadata value across all schemas
   */
  'filter:metadata' |

  /**
   * For searching in a specific metadata field.
   * Note: If the field is in a custom schema, the search implementation may
   * also require a formId filter for that schema.
   */
  'filter:metadataField' |

  'filter:missing' |
  'filter:notEmpty' |
  'filter:mediaDuration' |

  /**
   * Filter items that had their highres deleted using the 'highres deletion' API
   */
  'filter:highresDeletionState' |

  /**
   * Filter items based on the existence of the 'highresDeletion' property
   */
  'filter:highresDeletion' |
  'filter:aspectRatio' |
  'filter:hasRelations' |

  /**
   * Returns only non-current versions of items.
   */
  'filter:onlyNonCurrentVersions' |

  /**
   * Filter items by the upload status.
   */
  'filter:uploadStatus' |

  /**
   * Filter items by the Kelda configuration it was ingested by.
   */
  'filter:ingestedByKelda' |

  /**
   * Filter items that are pinned by the user.
   */
  'filter:pinned';

/**
 * All hardcoded filter keywords that we know are default and always valid.
 * This list therefore does not include keywords for "dynamic" filters like
 * for searching a specific fieldId.
 */
export enum StaticFilterKeywords {
  Label = 'label',
  DefaultTitle = 'default_title',
  Title = 'title',
  Person = 'person',
  ProfileId = 'profileId',
  Transcript = 'transcript',
  Says = 'says',
  Translation = 'timedTranslatedTranscript',
  DefaultDescription = 'default_description',
  Description = 'description',
  FormId = 'formId',
  DateRange = 'mediaCreatedOn',
  CreatedDate = 'createdOn',
  DefaultMediaCreatedOn = 'default_mediaCreatedOn',
  DefaultCreatedDate = 'default_createdOn',
  PlannedIngestTime = 'scheduledRecording.startTimeMs',
  ModifiedOn = 'modifiedOn',
  GeoLocation = 'geoLocation',
  ItemType = 'type',
  ItemState = 'itemState',
  Events = 'events',
  Text = 'text',
  Folder = 'folder',
  FileName = 'fileName',
  Archived = 'archived',
  Orphan = 'orphan',
  BucketName = 'sourceBucket',
  FolderId = 'folderId',
  ItemId = 'itemId',
  PurgeDate = 'purgeDate',
  VirtualClip = 'vclip',
  Owner = 'owner',
  Rule = 'rule',
  RuleContext = 'ruleContext',
  RuleValidity = 'ruleValid',
  Metadata = 'metadata',
  Missing = 'missing',
  NotEmpty = 'notEmpty',
  Duration = 'mediaDuration',
  State = 'state',
  HighresDeletionState = 'highresDeletionState',
  HighresDeletion = 'highresDeletion',
  AspectRatio = 'aspectRatio',
  HasRelations = 'hasRelations',
  OnlyNonCurrentVersions = 'onlyNonCurrentVersions',
  IngestedByKelda = 'ingestedByKelda',
  UploadStatus = 'uploadStatus',
  Pinned = 'pinned',
}

/**
 * All hardcoded filter keywords for file search (ie: file:mp4) that we know are default and always valid.
 * This list therefore does not include keywords for "dynamic" filters like
 * for searching a specific fieldId.
 */
export enum StaticFileKeywords {
  File = 'file'
}


/**
 * To distinguish between static filters and mdf field, we're using a specific prefix to indicate that this is a metadata field, it is not a static filter
 * For example, if we have a metadata field with field id "event", the filter key will be "metadatafield.event"
 * */
export const METADATA_FIELD_FILTER_PREFIX = 'metadatafield.';


const filterKeywordsToType: Record<StaticFilterKeywords, FilterType> = {
  [StaticFilterKeywords.Label]: 'filter:label',
  [StaticFilterKeywords.DefaultTitle]: 'filter:title',
  [StaticFilterKeywords.Title]: 'filter:title',
  [StaticFilterKeywords.Person]: 'filter:person',
  [StaticFilterKeywords.ProfileId]: 'filter:profileId',
  [StaticFilterKeywords.Transcript]: 'filter:transcript',
  [StaticFilterKeywords.Says]: 'filter:transcript',
  [StaticFilterKeywords.Translation]: 'filter:timedTranslatedTranscript',
  [StaticFilterKeywords.DefaultDescription]: 'filter:description',
  [StaticFilterKeywords.Description]: 'filter:description',
  [StaticFilterKeywords.FormId]: 'filter:formId',
  [StaticFilterKeywords.DateRange]: 'filter:mediaCreatedOn',
  [StaticFilterKeywords.DefaultMediaCreatedOn]: 'filter:mediaCreatedOn',
  [StaticFilterKeywords.CreatedDate]: 'filter:createdDate',
  [StaticFilterKeywords.DefaultCreatedDate]: 'filter:createdDate',
  [StaticFilterKeywords.PlannedIngestTime]: 'filter:scheduledRecording.startTimeMs',
  [StaticFilterKeywords.ModifiedOn]: 'filter:modifiedOn',
  [StaticFilterKeywords.GeoLocation]: 'filter:geoLocation',
  [StaticFilterKeywords.ItemType]: 'filter:itemType',
  [StaticFilterKeywords.ItemState]: 'filter:itemState',
  [StaticFilterKeywords.State]: 'filter:itemState',
  [StaticFilterKeywords.Events]: 'filter:events',
  [StaticFilterKeywords.Text]: 'filter:text',
  [StaticFilterKeywords.Folder]: 'filter:folder',
  [StaticFilterKeywords.FileName]: 'filter:fileName',
  [StaticFilterKeywords.Archived]: 'filter:archived',
  [StaticFilterKeywords.Orphan]: 'filter:orphan',
  [StaticFilterKeywords.BucketName]: 'filter:sourceBucket',
  [StaticFilterKeywords.FolderId]: 'filter:folderId',
  [StaticFilterKeywords.ItemId]: 'filter:itemId',
  [StaticFilterKeywords.PurgeDate]: 'filter:purgeDate',
  [StaticFilterKeywords.VirtualClip]: 'filter:vclip',
  [StaticFilterKeywords.Owner]: 'filter:owner',
  [StaticFilterKeywords.Rule]: 'filter:rule',
  [StaticFilterKeywords.RuleContext]: 'filter:ruleContext',
  [StaticFilterKeywords.RuleValidity]: 'filter:ruleValidity',
  [StaticFilterKeywords.Metadata]: 'filter:metadata',
  [StaticFilterKeywords.Missing]: 'filter:missing',
  [StaticFilterKeywords.NotEmpty]: 'filter:notEmpty',
  [StaticFilterKeywords.Duration]: 'filter:mediaDuration',
  [StaticFilterKeywords.HighresDeletionState]: 'filter:highresDeletionState',
  [StaticFilterKeywords.HighresDeletion]: 'filter:highresDeletion',
  [StaticFilterKeywords.AspectRatio]: 'filter:aspectRatio',
  [StaticFilterKeywords.HasRelations]: 'filter:hasRelations',
  [StaticFilterKeywords.OnlyNonCurrentVersions]: 'filter:onlyNonCurrentVersions',
  [StaticFilterKeywords.IngestedByKelda]: 'filter:ingestedByKelda',
  [StaticFilterKeywords.UploadStatus]: 'filter:uploadStatus',
  [StaticFilterKeywords.Pinned]: 'filter:pinned',
};

export function filterTypeForKey(filterKey: string): FilterType {
  const staticKeyType = filterKeywordsToType[filterKey as StaticFilterKeywords];
  if (staticKeyType) {
    return staticKeyType;
  }

  if (filterKey.startsWith('events.')) {
    const suffix = filterKey.substring('events.'.length);
    if (suffix.length === 0) {
      return 'filter:events';
    } else if (suffix.indexOf('.') < 0) {
      return 'filter:eventsForm';
    } else {
      return 'filter:eventsField';
    }
  }

  // We return 'metadataField' as the default, and then whether the filter key
  // actually matches any metadata fieldId is checked later.
  return 'filter:metadataField';
}

export type UserVisibleFilter = StaticFilterKeywords.Says | StaticFilterKeywords.Title | StaticFilterKeywords.Description | StaticFilterKeywords.State |
  StaticFilterKeywords.Label | StaticFilterKeywords.Person | StaticFilterKeywords.ItemType | StaticFilterKeywords.VirtualClip | StaticFilterKeywords.Owner |
  StaticFilterKeywords.Folder | StaticFilterKeywords.Events | StaticFilterKeywords.FileName | StaticFilterKeywords.Orphan | StaticFilterKeywords.Archived | StaticFilterKeywords.BucketName |
  StaticFilterKeywords.Rule | StaticFilterKeywords.RuleContext | StaticFilterKeywords.RuleValidity | StaticFilterKeywords.Metadata | StaticFilterKeywords.Missing | StaticFilterKeywords.NotEmpty |
  StaticFilterKeywords.Text | StaticFilterKeywords.Duration | StaticFilterKeywords.HighresDeletionState | StaticFilterKeywords.HighresDeletion | StaticFilterKeywords.ItemId | StaticFilterKeywords.OnlyNonCurrentVersions
  | StaticFilterKeywords.IngestedByKelda | StaticFilterKeywords.UploadStatus | StaticFilterKeywords.Pinned;

export interface Celebrity {
  celebrityId: string;
  name: string;
  maxConfidence: number;
  avgConfidence?: number;
  segments?: SearchHitSegment[];
  urls: string[];

  nameHighlighted?: string;
}

/**
 * Represents a search hit within an item. Used for visualizing
 * the labels in a search hit.
 */
export interface SearchHit {
  icon: string;
  color: string;
  content: string;
  field: string;
  hasMultipleSegments: boolean;
  id: string;
  score: number;
  segments: SearchHitSegment[];
  hit?: string;
  geoLocation?: GeoShape;

  tooltip?: string;
  mdfId?: string;
}

export interface Label {
  name: string;
  maxConfidence: number;
  segments: SearchHitSegment[];

  nameHighlighted?: string;

  top?: number;
  left?: number;
  width?: number;
  height?: number;
}


export type PersonSegment = SearchHitSegment & { imageId?: string };

export interface Person {
  personId: string;
  maxConfidence: number;
  avgConfidence?: number;
  name?: string;
  imageId?: string;
  segments?: PersonSegment[]; // This is made optional as we now support person detection for image.

  nameHighlighted?: string;
  primaryImageUrl?: string;
}

export enum SortOptions {
  TITLE = 'title',
  ITEM_CREATED_DATE = 'item_created_date',
  MEDIA_CREATED_DATE = 'media_created_date',
  PLANNED_INGEST_TIME = 'planned_ingest_time',
  MODIFIED_ON = 'modifiedOn',
  MEDIA_DURATION = 'media_duration',
  MEDIA_SIZE = 'media_size',
  RELEVANCE = 'relevance',
  ITEM_DELETION_DATE = 'item_deletion_date',
}

export enum SearchDateOption {
  ANYTIME = 'anytime',
  PAST_HOUR = 'past_hour',
  PAST_24_HOURS = 'past_24_hours',
  PAST_WEEK = 'past_week',
  PAST_MONTH = 'past_month',
  PAST_YEAR = 'past_year',
  PAST = 'past',
  FUTURE = 'future',
  CUSTOM = 'custom',
}

/**
 * Interface for defining relative date filters with semantic units
 */
export interface RelativeDateFilter {
  type: 'relative' | 'absolute';
  value?: number;              // The numeric value (1, 5, 30)
  unit?: 'minute' | 'hour' | 'day' | 'week' | 'month' | 'year'; // Semantic unit
  direction?: 'past' | 'future';
  absoluteDate?: Date;
}

export enum SortOrder {
  ASCENDING= 'asc',
  DESCENDING = 'desc'
}

export interface SortParameters {
  // It can be three types:
  // 1. One of the values from the enum SortOptions. This is used by the classic sort menu from item list view
  //    to sort with a single field.
  // 2. One of the MDF field UUIDs (when from custom schemas)
  //    or a default field identifier from enum SystemDefaultField (when from the Common schema)
  // 3. One of the values from the array systemFields, denoting properties from item JSON object body root, e.g. originalFileName
  property?: string;

  // 'asc' or 'desc'
  order: string;

  // false when it is from item body, e.g., item.originalFileName
  // true when it is an MDF field like item.metadata.formData[default_title, or some UUID].
  isCustomField?: boolean;

  fieldId?: string;
  mdfSource?: string;
}

export const isInstanceOfSortParameters = (obj: unknown): obj is SortParameters => {
  return isObject(obj)
    && 'property' in obj && typeof obj['property'] === 'string'
    && 'order' in obj && typeof obj['order'] === 'string'
    && (obj['order'] === SortOrder.DESCENDING.toString() || obj['order'] === SortOrder.ASCENDING.toString())
    && (!('isCustomField' in obj) || ('isCustomField' in obj && typeof obj['isCustomField']) === 'boolean');
};

export const extraSearchIndexedFieldsToFetchForSingleItem = [
  // these are stored in bucket. so we fetch from search to be able to avoid multiple bucket access, which can be slower than single query.
  'timedTranscript',
  'transcript',
  'timedTranslatedTranscript',
  'translatedTranscript',
  'detectedLabels',
  'webProxyAudioDownMixInfo',

  // Include isHidden field for single item search result
  'isHidden',
];

export const highresDeletionExistenceFilterValues = <const>['set', 'unset'];
export const possibleUploadStatuses = <const>['started', 'completed'];

export const possiblePinnedStates = <const>['true'];
export type PinnedItemStates = typeof possiblePinnedStates[number];
export const isPinnedItemState = (value: string): value is PinnedItemStates =>{
  return possiblePinnedStates.includes(value as PinnedItemStates);
};
